import React from "react";
import PropTypes from "prop-types";
import { InlineWaiting, IconButton, PureModal } from "components/ui";
import { IoAndroidRefresh } from "react-icons/lib/io";
import { MdUnfoldMore, MdUnfoldLess, MdAdd, MdList } from "react-icons/lib/md";
import HcpcRow from "./hcpc-row";
import AddHcpcHistoryRecord from "./add-hcpc-history-record";
import PatientExcludedHCPCS, {
  usePatientExcludedHCPCS
} from "./excluded-hcpcs";
import { PatientProfileContext } from "components/profile/context";

const HcpcHistory = ({ patientId, patient_product_lines }) => {
  const { fetchPatientHCPCHistoryAPI } = React.useContext(
    PatientProfileContext
  );

  const [selectedProductLine, setSelectedProductLine] = React.useState(
    patient_product_lines[0]?.text ?? ""
  );

  const [expandedRows, setExpandedRows] = React.useState({});
  const [editRow, setEditRow] = React.useState(null);

  const toggleExpanded = productType => {
    const current = expandedRows[productType] ?? false;
    setExpandedRows({ ...expandedRows, [productType]: !current });
    setEditRow(null);
  };

  const allExpanded =
    !Object.values(expandedRows).some(x => x === false) ||
    Object.values(expandedRows).length === 0;

  React.useEffect(() => {
    setEditRow(null);
  }, [expandedRows, patientId]);

  const orderHistory = fetchPatientHCPCHistoryAPI.response?.eligiblity ?? [];

  const excludedPatientHCPCInfo = usePatientExcludedHCPCS(patientId);

  return (
    <div className="patient-hcpc-history-tab">
      {fetchPatientHCPCHistoryAPI.isFetching ? (
        <div
          style={{
            minHeight: 300,
            display: "flex",
            flex: 1,
            alignContent: "center",
            justifyContent: "center"
          }}
        >
          <InlineWaiting />
        </div>
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "100px 1fr auto",
              alignItems: "center"
            }}
          >
            <div>
              <IconButton
                icon={<IoAndroidRefresh size={18} />}
                disabled={fetchPatientHCPCHistoryAPI.isFetching}
                onClick={() => {
                  fetchPatientHCPCHistoryAPI.fetchData(patientId);
                }}
              />
              <IconButton
                icon={
                  allExpanded ? (
                    <MdUnfoldLess size={20} />
                  ) : (
                    <MdUnfoldMore size={20} />
                  )
                }
                onClick={() => {
                  setEditRow(null);
                  if (allExpanded) {
                    setExpandedRows(
                      orderHistory.reduce(
                        (acc, x) => ({ ...acc, [x.productType]: false }),
                        {}
                      )
                    );
                  } else {
                    setExpandedRows(
                      orderHistory.reduce(
                        (acc, x) => ({ ...acc, [x.productType]: true }),
                        {}
                      )
                    );
                  }
                }}
              />
            </div>
            <div />
            <div style={{ display: "flex", alignItems: "center" }}>
              <label
                style={{
                  fontSize: "1.25em",
                  paddingRight: 10
                }}
              >
                Product Line:
              </label>
              {patient_product_lines.some(x => x.text == "CPAP") && (
                <span>
                  <label
                    style={{
                      alignItems: "center",
                      cursor: "pointer",
                      justifyContent: "center",
                      fontSize: "1.15em",
                      display: "inline-flex",
                      paddingRight: 20
                    }}
                  >
                    CPAP
                    <input
                      type="radio"
                      style={{ margin: "0px 10px" }}
                      checked={selectedProductLine == "CPAP"}
                      onClick={() => {
                        setSelectedProductLine("CPAP");
                      }}
                    />
                  </label>
                </span>
              )}
              {patient_product_lines.some(x => x.text == "CGM") && (
                <span>
                  <label
                    style={{
                      alignItems: "center",
                      cursor: "pointer",
                      justifyContent: "center",
                      fontSize: "1.15em",
                      display: "inline-flex",
                      paddingRight: 20
                    }}
                  >
                    CGM
                    <input
                      type="radio"
                      style={{ margin: "0px 10px" }}
                      checked={selectedProductLine == "CGM"}
                      onClick={() => {
                        setSelectedProductLine("CGM");
                      }}
                    />
                  </label>
                </span>
              )}
              {patient_product_lines.length > 1 && (
                <span>
                  <label
                    style={{
                      alignItems: "center",
                      cursor: "pointer",
                      justifyContent: "center",
                      fontSize: "1.15em",
                      display: "inline-flex",
                      paddingRight: 20
                    }}
                  >
                    All
                    <input
                      type="radio"
                      style={{ margin: "0px 10px" }}
                      checked={selectedProductLine == ""}
                      onClick={() => {
                        setSelectedProductLine("");
                      }}
                    />
                  </label>
                </span>
              )}
            </div>
            <span style={{ display: "flex", alignItems: "center" }}></span>
          </div>
          <hr style={{ margin: 0 }} />
          <div className="eligibility-table-header">
            <div>Product Type</div>
            <div>Eligibility Date</div>
            <div>Last Ordered Date</div>
            <div>Last Ordered Item</div>
            <div>Last Ordered Item ID</div>
            <div>Insurance Rule(days|qty|offset)</div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flex: 1,
                justifyContent: "space-between"
              }}
            ></div>
          </div>
          {orderHistory
            ?.filter(
              x =>
                x.productType !== "Mask with Headgear" &&
                (!selectedProductLine || x.productLine === selectedProductLine)
            )
            .map(x => (
              <HcpcRow
                key={x.productType}
                eligibility={x}
                toggleExpanded={() => toggleExpanded(x.productType)}
                expanded={expandedRows[x.productType]}
                editRow={editRow}
                setEditRow={setEditRow}
                patientId={patientId}
              />
            ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          justifyContent: "space-between",
          marginRight: 20
        }}
      >
        <PureModal
          renderTrigger={({ openModal }) => (
            <IconButton
              icon={<MdList />}
              text={`Excluded HCPCS (${excludedPatientHCPCInfo.excludedHcpcsCount})`}
              onClick={e => {
                e.stopPropagation();
                openModal();
              }}
              style={{ fontSize: "14px", float: "right" }}
            />
          )}
        >
          <PatientExcludedHCPCS
            patientId={patientId}
            {...excludedPatientHCPCInfo}
          />
        </PureModal>
        <PureModal
          renderTrigger={({ openModal }) => (
            <IconButton
              icon={<MdAdd />}
              text="Add Record"
              onClick={e => {
                e.stopPropagation();
                openModal();
              }}
              style={{ fontSize: "14px", float: "right" }}
            />
          )}
          renderContent={({ closeModal }) => (
            <AddHcpcHistoryRecord
              patientId={patientId}
              onSuccess={async () => {
                await fetchPatientHCPCHistoryAPI.fetchData(patientId);
                closeModal();
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

HcpcHistory.propTypes = {
  patientId: PropTypes.string.isRequired,
  patient_product_lines: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default HcpcHistory;
